import { IsOEPGuid } from '../contracts/ids.js';
import type { OEPPhase } from '../workflow/phases.js';
import { IsWindowOpen } from '../workflow/windows.js';

/** Outcome of one entity rule. Pure — the entity wrappers turn a failure into a ValidationErrorInfo. */
export type RuleDecision<T = void> = { Ok: true; Value: T } | { Ok: false; Field: string; Message: string };
const ok = <T>(Value: T): RuleDecision<T> => ({ Ok: true, Value });
const no = <T>(Field: string, Message: string): RuleDecision<T> => ({ Ok: false, Field, Message });

/** A column value as the rules see it (RunView rows and entity getters are normalized to this). */
export type OEPRuleScalar = string | number | boolean | null;
/** A (partial) row keyed by column name. */
export type OEPRuleRow = Readonly<Record<string, OEPRuleScalar>>;

export type OEPReviewStatus = 'NotStarted' | 'InProgress' | 'Completed' | 'Submitted';
export type OEPDraftOrSubmitted = 'Draft' | 'Submitted';
const THREAD_LIMIT = 3;

/**
 * Columns a non-staff user may never change on update, compared against the fresh DB row
 * (never BaseEntity OldValue — the client supplies it when TrackRecordChanges=0).
 * Every FK column of these entities is here (decisions.spec.ts drift guard reads the generated
 * schemas), plus the server-assigned ThreadPosition / Slot.
 */
export const OEP_IMMUTABLE_COLUMNS = {
    Nominations: ['SeasonID', 'PresentationID', 'NominatorUserID'],
    NominationResponses: ['NominationID', 'ReviewQuestionID'],
    CommitteeSelections: ['SeasonID', 'SectionID'],
    CommitteeSelectionItems: ['CommitteeSelectionID', 'PresentationID', 'Slot'],
    Conversations: ['ReviewID'],
    ConversationMessages: ['ConversationID', 'SenderUserID', 'ThreadPosition'],
    Reviews: ['ReviewAssignmentID'],
    ReviewResponses: ['ReviewID', 'ReviewQuestionID'],
    ReviewThankYouNotes: ['ReviewID'],
    // Final review C1: a v1 (archive) row can never be moved INTO an OEP season by a non-staff saver — MJ's
    // Update RLS checks only the row as stored (GenericDatabaseProvider CheckUpdateRLS), not the new values.
    Presentations: ['SeasonID', 'SectionID', 'SessionID'],
} as const satisfies Readonly<Record<string, readonly string[]>>;
export type OEPImmutableEntityKey = keyof typeof OEP_IMMUTABLE_COLUMNS;

/**
 * FK columns deliberately NOT immutable, with why:
 * - ReviewResponse.RubricLevelID is the reviewer's answer (a chosen rubric level), not a link to a parent.
 * - Presentation.PresenterUserID / PresentationCategoryID are v1 archive edits (see the map below).
 * - CommitteeSelection.SubmittedByUserID is stamped on submit by the client (T26 plan); it is a
 *   self-stamp column — a non-staff saver may only set it to themselves (DecideForeignKeysUnchanged SelfStampFields).
 */
export const OEP_MUTABLE_FOREIGN_KEYS: Readonly<Partial<Record<OEPImmutableEntityKey, readonly string[]>>> = {
    ReviewResponses: ['RubricLevelID'],
    CommitteeSelections: ['SubmittedByUserID'],
    // v1 edits its own archive rows (category re-detection, admin re-assignment); OEP rows are out of a v1 role's
    // reach by RLS (tools/metadata/oep-permissions.js V1_ARCHIVE_SCOPE) and OEP participants hold no Update grant.
    Presentations: ['PresenterUserID', 'PresentationCategoryID'],
};


/** Column equality as SQL Server sees it: GUIDs case-insensitive, bit ~ boolean, null only equals null. */
export function SameRuleValue(a: OEPRuleScalar | undefined, b: OEPRuleScalar | undefined): boolean {
    const x = a ?? null, y = b ?? null;
    if (x === null || y === null) return x === y;
    if (typeof x === 'boolean' || typeof y === 'boolean') return Number(x) === Number(y);
    if (typeof x === 'string' && typeof y === 'string' && IsOEPGuid(x) && IsOEPGuid(y)) return x.toUpperCase() === y.toUpperCase();
    return x === y;
}

/** FK columns are immutable for non-staff on update; self-stamp columns may only be set to the saver. */
export function DecideForeignKeysUnchanged(i: {
    IsStaffOrSystem: boolean; IsNew: boolean; Current: OEPRuleRow | null; Proposed: OEPRuleRow;
    Fields: readonly string[]; SelfStampFields?: readonly string[]; SaverUserID: string;
}): RuleDecision {
    if (i.IsStaffOrSystem) return ok(undefined);
    if (i.IsNew) {
        // No prior row: a self-stamp column may only be empty or the saver.
        for (const f of i.SelfStampFields ?? []) {
            const proposed = i.Proposed[f] ?? null;
            if (proposed !== null && (typeof proposed !== 'string' || !SameRuleValue(proposed, i.SaverUserID))) return no(f, 'This link cannot be changed.');
        }
        return ok(undefined);
    }
    if (!i.Current) return no('ID', 'This record is no longer available.');
    for (const f of i.Fields) {
        if (!SameRuleValue(i.Current[f], i.Proposed[f])) return no(f, 'This link cannot be changed.');
    }
    for (const f of i.SelfStampFields ?? []) {
        const proposed = i.Proposed[f] ?? null;
        if (SameRuleValue(i.Current[f], proposed)) continue;
        if (typeof proposed !== 'string' || !SameRuleValue(proposed, i.SaverUserID)) return no(f, 'This link cannot be changed.');
    }
    return ok(undefined);
}

/**
 * Fail closed: when a parent/season lookup made as the saver comes back empty, a non-staff save
 * or delete is refused rather than skipping the rules. Staff/system are unaffected.
 */
export function DecideParentReadable(i: { IsStaffOrSystem: boolean; Found: boolean; Field: string }): RuleDecision {
    if (i.IsStaffOrSystem || i.Found) return ok(undefined);
    return no(i.Field, 'This record is not available to you.');
}

/** On create, a non-staff user writes rows only as themselves (sender, nominator). */
export function DecideOwnedBySaver(i: { IsStaffOrSystem: boolean; Field: string; OwnerUserID: string; SaverUserID: string }): RuleDecision {
    if (i.IsStaffOrSystem || SameRuleValue(i.OwnerUserID, i.SaverUserID)) return ok(undefined);
    return no(i.Field, 'You can only save this as yourself.');
}

/** Thread rules, copy from logic.js:563-570. `Existing` = sender IDs in position order. */
export function DecideThreadMessage(i: { Phase: OEPPhase; ReviewStatus: string; PresenterUserID: string; Existing: readonly string[]; SenderUserID: string }): RuleDecision<{ Position: number }> {
    if (!IsWindowOpen('Thread', i.Phase) || i.ReviewStatus !== 'Completed') return no('ConversationID', 'Conversations open once feedback is released.');
    if (i.Existing.length >= THREAD_LIMIT) return no('ConversationID', 'This conversation is complete. Three messages is the limit.');
    if (i.Existing.length === 0 && !SameRuleValue(i.SenderUserID, i.PresenterUserID)) return no('SenderUserID', 'The presenter starts the conversation.');
    if (i.Existing.length > 0 && SameRuleValue(i.Existing[i.Existing.length - 1], i.SenderUserID)) {
        return no('SenderUserID', SameRuleValue(i.SenderUserID, i.PresenterUserID) ? 'Sent. Your reviewer can reply once more.' : 'Sent. The presenter can reply once more.');
    }
    return ok({ Position: i.Existing.length + 1 });
}

export function DecideConversationCreate(i: { Phase: OEPPhase; ReviewStatus: string; ExistingCount: number }): RuleDecision {
    if (!IsWindowOpen('Thread', i.Phase) || i.ReviewStatus !== 'Completed') return no('ReviewID', 'Conversations open once feedback is released.');
    if (i.ExistingCount > 0) return no('ReviewID', 'You can start one conversation with this reviewer.');
    return ok(undefined);
}

export function DecideThankYou(i: { Phase: OEPPhase; ReviewStatus: string }): RuleDecision {
    if (!IsWindowOpen('ThankYou', i.Phase) || i.ReviewStatus !== 'Completed') return no('ReviewID', 'You can thank a reviewer once their feedback is released.');
    return ok(undefined);
}

/** `OldStatus` is the status in the fresh DB row (null for a new review). */
export function DecideReviewChange(i: { Phase: OEPPhase; IsStaffOrSystem: boolean; IsNew: boolean; OldStatus: OEPReviewStatus | null; NewStatus: OEPReviewStatus; ExistingForAssignment: number; ResponsesComplete: boolean }): RuleDecision {
    if (i.IsNew && i.ExistingForAssignment > 0) return no('ReviewAssignmentID', 'A review already exists for this assignment.');
    if (i.IsStaffOrSystem) return ok(undefined);
    if (i.NewStatus === 'Completed') return no('Status', 'Feedback is released by AGU staff.');
    if (!IsWindowOpen('Review', i.Phase)) return no('Status', 'The feedback window is closed.');
    if (i.OldStatus === 'Submitted' || i.OldStatus === 'Completed') return no('Status', 'Submitted feedback cannot be changed.');
    if (i.NewStatus === 'Submitted' && !i.ResponsesComplete) return no('Status', 'Score all five areas with a comment, and describe the primary strengths, before submitting.');
    return ok(undefined);
}

export function DecideReviewResponseChange(i: { Phase: OEPPhase; IsStaffOrSystem: boolean; ReviewStatus: string; NumericValue: number | null; Min: number | null; Max: number | null }): RuleDecision {
    if (i.NumericValue !== null && ((i.Min !== null && i.NumericValue < i.Min) || (i.Max !== null && i.NumericValue > i.Max))) return no('NumericValue', 'Rating is outside the scale.');
    if (i.IsStaffOrSystem) return ok(undefined);
    if (!IsWindowOpen('Review', i.Phase)) return no('ReviewID', 'The feedback window is closed.');
    if (i.ReviewStatus === 'Submitted' || i.ReviewStatus === 'Completed') return no('ReviewID', 'Submitted feedback cannot be changed.');
    return ok(undefined);
}

/** A nomination belongs to its presentation's season (create and update, everyone). */
export function DecideNominationSeason(i: { NominationSeasonID: string; PresentationFound: boolean; PresentationSeasonID: string | null }): RuleDecision {
    if (!i.PresentationFound) return no('PresentationID', 'This presentation is not available for nomination.');
    if (i.PresentationSeasonID === null || !SameRuleValue(i.NominationSeasonID, i.PresentationSeasonID)) return no('SeasonID', 'A nomination must be in its presentation\'s season.');
    return ok(undefined);
}

/** `OldStatus` is the status in the fresh DB row (null for a new nomination). */
export function DecideNominationChange(i: { Phase: OEPPhase; IsStaffOrSystem: boolean; OldStatus: OEPDraftOrSubmitted | null; NewStatus: OEPDraftOrSubmitted; Attested: boolean; RatedCount: number; OutstandingWithoutJustification: number }): RuleDecision {
    if (i.IsStaffOrSystem) return ok(undefined);
    // Editable (and withdrawable back to Draft) until nominations close — logic.js:697 nomDoneBody.
    if (!IsWindowOpen('Nomination', i.Phase)) return no('Status', 'Nominations are closed.');
    if (i.NewStatus === 'Submitted') {
        if (i.RatedCount < 5) return no('Status', 'Score all five criteria before submitting.');
        if (i.OutstandingWithoutJustification > 0) return no('Status', 'Justify each outstanding rating in two to four sentences.');
        if (!i.Attested) return no('NoConflictAttested', 'Confirm you have no conflict of interest with this presenter.');
    }
    return ok(undefined);
}

export function DecideNominationResponseChange(i: { Phase: OEPPhase; NominationStatus: OEPDraftOrSubmitted }): RuleDecision {
    void i.NominationStatus; // editable in both states while the window is open (logic.js:697)
    if (!IsWindowOpen('Nomination', i.Phase)) return no('NominationID', 'Nominations are closed.');
    return ok(undefined);
}

/** `OldStatus` is the status in the fresh DB row (null for a new slate). */
export function DecideSelectionChange(i: { Phase: OEPPhase; IsStaffOrSystem: boolean; OldStatus: OEPDraftOrSubmitted | null; NewStatus: OEPDraftOrSubmitted; ItemCount: number }): RuleDecision {
    if (i.IsStaffOrSystem) return ok(undefined);
    if (!IsWindowOpen('Selection', i.Phase)) return no('Status', 'Winner selection is closed.');
    if (i.OldStatus === 'Submitted') return no('Status', 'Submitted. Contact the OEP team if your committee needs to change its slate.');
    if (i.NewStatus === 'Submitted' && i.ItemCount === 0) return no('Status', 'Select at least one winner before submitting.');
    return ok(undefined);
}

/**
 * `SelectionSectionID` / `PresentationSectionID` are fresh DB reads (the slate's section, the
 * presentation's Presentation.SectionID): a committee member who sits on two sections' committees
 * (UQ_CommitteeMember_Season_Section_User allows it) can read both sections, so RLS alone does not
 * keep a pick on the right slate.
 */
export function DecideSelectionItemAdd(i: {
    Phase: OEPPhase; SelectionStatus: OEPDraftOrSubmitted; UsedSlots: readonly number[]; Cap: number; PresentationHasSubmittedNomination: boolean;
    SelectionSectionID: string; PresentationSectionID: string | null;
}): RuleDecision<{ Slot: number }> {
    if (!IsWindowOpen('Selection', i.Phase)) return no('CommitteeSelectionID', 'Winner selection is closed.');
    if (i.SelectionStatus === 'Submitted') return no('CommitteeSelectionID', 'Submitted. Contact the OEP team if your committee needs to change its slate.');
    if (!i.PresentationHasSubmittedNomination) return no('PresentationID', 'Winners are selected from nominated presentations.');
    if (i.PresentationSectionID === null || !SameRuleValue(i.PresentationSectionID, i.SelectionSectionID)) {
        return no('PresentationID', 'Winners are selected from your own section\'s nominated presentations.');
    }
    for (let slot = 1; slot <= i.Cap; slot++) if (!i.UsedSlots.includes(slot)) return ok({ Slot: slot });
    return no('CommitteeSelectionID', `${i.Cap} is the maximum for this section. Deselect one if your committee wants to swap.`);
}

export function DecideSelectionItemRemove(i: { Phase: OEPPhase; SelectionStatus: OEPDraftOrSubmitted; IsStaffOrSystem: boolean }): RuleDecision {
    if (i.IsStaffOrSystem) return ok(undefined);
    if (!IsWindowOpen('Selection', i.Phase)) return no('CommitteeSelectionID', 'Winner selection is closed.');
    if (i.SelectionStatus === 'Submitted') return no('CommitteeSelectionID', 'Submitted. Contact the OEP team if your committee needs to change its slate.');
    return ok(undefined);
}

/** `ChangedFields` = columns whose proposed value differs from the fresh DB row. */
export function DecideParticipantChange(i: { Phase: OEPPhase; IsStaffOrSystem: boolean; ChangedFields: readonly string[] }): RuleDecision {
    if (i.IsStaffOrSystem) return ok(undefined);
    const other = i.ChangedFields.filter((f) => f !== 'CareerStage' && !f.startsWith('__mj_'));
    if (other.length > 0) return no(other[0], 'Only your career stage can be changed here.');
    if (i.ChangedFields.includes('CareerStage') && !IsWindowOpen('CareerStage', i.Phase)) return no('CareerStage', 'Career stage is set with your draft upload.');
    return ok(undefined);
}
